import { BlockBlobClient } from '@azure/storage-blob'
import { BlockBlobParallelUploadOptions } from '@azure/storage-blob'
import type { AdjustmentAttendanceFormState } from '../types/Schedule/EmployeeScheduleCalendar'
import { CardSignInRequest, DirectReportAttendance } from '../types/Employee/Attendance'
import { EmployeeAttendance } from '../types/Employee/Employee'
import { WorkArea } from '../types/Project/WorkArea'
import { AttachmentsUploadResponse } from '../types/Upload/AttachmentResponse'
import {
	getAll,
	patch,
	patchWithResponse,
	post,
	postWithResponse,
	putWithResponse,
} from './CommonServices/httpService'
import { ForemanDB } from '../types/IndexedDB/ForemanDB'
import {
	getDirectReportsAttendanceFB,
	revertSignOutWorkersFB,
	signOutWorkersBulkFB,
} from './CacheFallbackServices/attendanceFallback'
import { mapAttendance } from './CacheMapperServices/attendanceMapper'
import { hasConnection } from './CommonServices/healthGate'
import { signInWorkersBulkFB } from './CacheFallbackServices/attendanceFallback'
import { showErrorToast } from './CommonServices/alertServices'

export const getSupervisorAttendanceRecords = async (supervisorId: number) => {
	const result = await getAll<EmployeeAttendance[]>(`/Attendance/${supervisorId}`)

	return result.data
}

export const getWorkAreas = async () => {
	const result = await getAll<WorkArea[]>(`/Area`)

	return result.data
}

export const signInWorkerByTask = async (workerId: number, taskType: string, taskId?: number) => {
	const result = await post(`/Attendance/SignIn`, { workerId, taskId, taskType })
	return result.success
}

export const getDirectReportsAttendance = async (
	department: string,
	supervisorId?: number,
	signOut?: boolean,
	db?: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db) {
		try {
			return await getDirectReportsAttendanceFB(db, signOut)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get attendance from cache' })
			throw error
		}
	}
	const result = await getAll<DirectReportAttendance[]>(
		`/Attendance/Supervisor?dept=${department}${
			supervisorId ? `&supervisorId=${supervisorId}` : ''
		}${signOut ? '&signOut=true' : ''}`,
	)
	if (db && !signOut) {
		mapAttendance(db, result.data)
	}
	return result.data
}

export const signInWorkersBulk = async (
	newAttendance: DirectReportAttendance[],
	consentForms?: { workerId: number; blob: Blob }[],
	db?: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db) {
		try {
			return await signInWorkersBulkFB(db, newAttendance, consentForms)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to sign in workers in offline mode' })
			throw error
		}
	}
	const result = await postWithResponse<AttachmentsUploadResponse[]>(
		`/Attendance/SignIn/Bulk`,
		newAttendance,
	)
	if (consentForms) {
		await uploadConsentForm(consentForms, result.data)
		return true
	}

	return result.success
}

export const signOutWorkersBulk = async (
	workerIds: number[],
	consentForms?: { workerId: number; blob: Blob }[],
	db?: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db) {
		try {
			return await signOutWorkersBulkFB(db, workerIds, consentForms)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to sign out workers in offline mode' })
			throw error
		}
	}
	const result = await patchWithResponse<AttachmentsUploadResponse[]>(
		`/Attendance/SignOut/Bulk`,
		workerIds,
	)
	if (consentForms) {
		await uploadConsentForm(consentForms, result.data)
		return true
	}
	return result.success
}

export const revertSignOut = async (workerId: number, db?: ForemanDB | null) => {
	if (!(await hasConnection()) && db) {
		try {
			await revertSignOutWorkersFB(db, workerId)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to revert sign out in offline mode' })
			return false
		}
		return true
	}
	const result = await patch(`/Attendance/SignOut/Revert/${workerId}`, {})
	return result.success
}

export const cardSignIn = async (
	cardSignInRequest: CardSignInRequest,
	consentForms?: { workerId: number; blob: Blob }[],
) => {
	const result = await postWithResponse<AttachmentsUploadResponse>(`/Cards`, cardSignInRequest)
	if (consentForms) {
		await uploadConsentForm(consentForms, [result.data])
		return true
	}
	return result.success
}

export const cardSignOut = async (
	cardSignInRequest: CardSignInRequest,
	consentForms?: { workerId: number; blob: Blob }[],
) => {
	const result = await patchWithResponse<AttachmentsUploadResponse>(`/Cards`, cardSignInRequest)
	if (consentForms) {
		await uploadConsentForm(consentForms, [result.data])
		return true
	}
	return result.success
}

const uploadConsentForm = async (
	consentForms: { workerId: number; blob: Blob }[],
	tokens: AttachmentsUploadResponse[],
) => {
	const uploadTasks = consentForms.map((form) => {
		const token = tokens.find((a) => a.id === form.workerId)

		// Create a BlobClient for the current file
		const blockBlobClient = new BlockBlobClient(`${token?.uploadUri}${token?.sasToken}`)

		const options: BlockBlobParallelUploadOptions = {
			blobHTTPHeaders: { blobContentType: form.blob.type },
		}

		// Upload file
		return blockBlobClient.uploadData(form.blob, options)
	})
	await Promise.all(uploadTasks)
}

//#region Calendar Attendance

export const getScheduleCalendarAttendance = async (
	workerId: number,
	date: string,
): Promise<AdjustmentAttendanceFormState> => {
	const result = await getAll<AdjustmentAttendanceFormState>(
		`/Attendance/calendar?workerId=${workerId}&date=${encodeURIComponent(date)}`,
	)

	if (!result.success || result.data == null) {
		throw new Error('Failed to load schedule calendar attendance')
	}

	return result.data
}

export const saveScheduleCalendarAttendance = async (
	workerId: number,
	date: string,
	form: AdjustmentAttendanceFormState,
): Promise<AdjustmentAttendanceFormState> => {
	if (!form.status) throw new Error('Attendance status is required.')

	const result = await putWithResponse<AdjustmentAttendanceFormState>(`/Attendance/calendar`, {
		workerId,
		date,
		status: form.status,
		signInTime: form.signInTime || null,
		signOutTime: form.signOutTime || null,
	})

	if (!result.success || result.data == null) {
		throw new Error('Failed to save schedule calendar attendance')
	}

	return result.data
}

export const saveScheduleCalendarAttendanceBulk = async (
	date: string,
	workerIds: number[],
	form: AdjustmentAttendanceFormState,
): Promise<void> => {
	if (!form.status) throw new Error('Attendance status is required.')

	const result = await putWithResponse<unknown>(`/Attendance/calendar/bulk`, {
		date,
		workerIds,
		status: form.status,
		signInTime: form.signInTime || null,
		signOutTime: form.signOutTime || null,
	})

	if (!result.success) {
		throw new Error('Failed to save schedule calendar attendance')
	}
}

//#endregion
